import { type RefCallback, useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import type { ExtendedListing } from '@/src/lib/api/endpoints/listing';
import type { SrpAdSlotPlacement } from '@/src/lib/ads/constants';
import { getVisualViewportBottom } from '@/src/lib/utils/viewport';

function isContentBottomBelowViewport(contentBottom: number): boolean {
  return contentBottom >= getVisualViewportBottom();
}

/**
 * A live refetch that lands fewer results shrinks the listings region and
 * pulls the pagination / area content / footer up into the viewport — the
 * dominant grid-mode CLS source in RUM attribution. Pin the region to its
 * pre-swap height when the vacated space would be visible; the release waits
 * until the natural bottom has left the viewport (same below-viewport rule as
 * SrpAdSlot's collapse), so neither the pin nor the release is scored. The ref
 * attaches to the region wrapper — not the grid <ul> — so the pin survives the
 * grid ↔ empty-region branch swap at the zero-results boundary.
 */
export function useGridHeightPin({
  listings,
  adPlacements,
}: {
  listings: Array<ExtendedListing>;
  adPlacements: ReadonlyArray<SrpAdSlotPlacement>;
}): RefCallback<HTMLElement> {
  const gridRef = useRef<HTMLElement | null>(null);
  const renderedHeightRef = useRef<number | null>(null);
  const isPinnedRef = useRef(false);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // The bookkeeping describes one DOM element. The grid unmounts on the
  // feed toggle and the empty state while this hook stays mounted, so state
  // must reset on attach/detach or a remounted grid inherits a phantom pin.
  const setGridRef = useCallback<RefCallback<HTMLElement>>((node) => {
    if (node === gridRef.current) return;
    if (gridRef.current) resizeObserverRef.current?.unobserve(gridRef.current);
    gridRef.current = node;
    renderedHeightRef.current = null;
    isPinnedRef.current = false;
    if (!node) return;
    if (!resizeObserverRef.current && typeof ResizeObserver !== 'undefined') {
      // The grid's height also changes without a listings/adPlacements commit
      // (ad collapse, late image loads); keep the un-pinned baseline fresh so
      // the next pin decision compares against the height the user last saw.
      resizeObserverRef.current = new ResizeObserver(() => {
        const grid = gridRef.current;
        if (!grid || isPinnedRef.current) return;
        renderedHeightRef.current = grid.getBoundingClientRect().height;
      });
    }
    resizeObserverRef.current?.observe(node);
  }, []);

  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    const previousRenderedHeight = renderedHeightRef.current;
    // min-height is set only while pinned, so skip the write otherwise — an
    // unconditional removeProperty dirties style on every commit and turns the
    // measurement below into a forced style recalc + reflow.
    if (isPinnedRef.current) grid.style.removeProperty('min-height');
    const rect = grid.getBoundingClientRect();
    const naturalHeight = rect.height;

    const shouldPin =
      previousRenderedHeight !== null &&
      naturalHeight < previousRenderedHeight &&
      !isContentBottomBelowViewport(rect.bottom);

    if (shouldPin) {
      grid.style.minHeight = `${previousRenderedHeight}px`;
    }
    isPinnedRef.current = shouldPin;
    // When pinned, the rendered box keeps the previous height (min-height only
    // ever raises it), so the baseline only moves when un-pinned.
    if (!shouldPin) renderedHeightRef.current = naturalHeight;
  }, [listings, adPlacements]);

  useEffect(() => {
    let animationFrame = 0;

    const maybeReleasePin = () => {
      animationFrame = 0;
      const grid = gridRef.current;
      if (!grid || !isPinnedRef.current) return;
      // Content can reflow while pinned (image loads, ad fill/collapse), so
      // measure the live content edge — the pinned box hides the natural
      // height — rather than trusting the pin-time value; a stale value could
      // release while content is visible. Max over children, not the last
      // child alone, so the check holds under any column count or alignment.
      let contentBottom = grid.getBoundingClientRect().top;
      for (const child of grid.children) {
        const bottom = child.getBoundingClientRect().bottom;
        if (bottom > contentBottom) contentBottom = bottom;
      }
      if (isContentBottomBelowViewport(contentBottom)) {
        grid.style.removeProperty('min-height');
        isPinnedRef.current = false;
        renderedHeightRef.current = grid.getBoundingClientRect().height;
      }
    };

    // Capture phase: the SRP scrollport is the content panel, and scroll
    // events don't bubble — a window listener only sees them via capture.
    const requestRelease = () => {
      if (!isPinnedRef.current || animationFrame) return;
      animationFrame = window.requestAnimationFrame(maybeReleasePin);
    };
    window.addEventListener('scroll', requestRelease, { capture: true, passive: true });
    return () => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('scroll', requestRelease, { capture: true });
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
    };
  }, []);

  return setGridRef;
}
